import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth, useLanguage, API } from "../App";
import { AppLayout } from "../components/AppLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Building2, CheckSquare, AlertTriangle, FileText, Clock, Plus,
  TrendingUp, Users, ArrowRight, DollarSign, Shield, Timer,
  Settings, ChevronUp, ChevronDown, X, GripVertical, Eye
} from "lucide-react";
import { toast } from "sonner";

const DEFAULT_WIDGETS = [
  { id: "stats", label_sv: "KPI-kort", label_en: "KPI Cards", visible: true, order: 0 },
  { id: "quick_actions", label_sv: "Snabbåtgärder", label_en: "Quick Actions", visible: true, order: 1 },
  { id: "task_progress", label_sv: "Uppgiftsframsteg", label_en: "Task Progress", visible: true, order: 2 },
  { id: "incidents", label_sv: "Avvikelser", label_en: "Incidents", visible: true, order: 3 },
  { id: "budget", label_sv: "Ekonomi", label_en: "Budget", visible: true, order: 4 },
  { id: "time_week", label_sv: "Tid denna vecka", label_en: "Time This Week", visible: true, order: 5 },
  { id: "recent_incidents", label_sv: "Senaste avvikelser", label_en: "Recent Incidents", visible: true, order: 6 },
];

export const DashboardPage = () => {
  const { user, token } = useAuth();
  const { language, t } = useLanguage();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [widgets, setWidgets] = useState(DEFAULT_WIDGETS);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const sv = (s, e) => language === "sv" ? s : e;

  useEffect(() => { fetchSummary(); fetchDashboardConfig(); }, []);

  const fetchSummary = async () => {
    try {
      const res = await fetch(`${API}/dashboard/summary`, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" });
      if (res.ok) setSummary(await res.json());
    } catch (err) { console.error("Failed:", err); }
    finally { setLoading(false); }
  };

  const fetchDashboardConfig = async () => {
    try {
      const res = await fetch(`${API}/user/dashboard-config`, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        if (data.widgets?.length) {
          const merged = DEFAULT_WIDGETS.map(dw => {
            const saved = data.widgets.find(w => w.id === dw.id);
            return saved ? { ...dw, visible: saved.visible, order: saved.order } : dw;
          }).sort((a, b) => a.order - b.order);
          setWidgets(merged);
        }
      }
    } catch (err) { console.error("Failed to load config:", err); }
  };

  const saveDashboardConfig = async (newWidgets) => {
    try {
      const payload = newWidgets.map(w => ({ id: w.id, visible: w.visible, order: w.order }));
      await fetch(`${API}/user/dashboard-config`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        credentials: "include",
        body: JSON.stringify({ widgets: payload })
      });
    } catch (err) { console.error("Failed to save config:", err); }
  };

  const toggleWidget = (id) => {
    const updated = widgets.map(w => w.id === id ? { ...w, visible: !w.visible } : w);
    setWidgets(updated);
    saveDashboardConfig(updated);
  };

  const moveWidget = (id, direction) => {
    const idx = widgets.findIndex(w => w.id === id);
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= widgets.length) return;
    const updated = [...widgets];
    [updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]];
    const reordered = updated.map((w, i) => ({ ...w, order: i }));
    setWidgets(reordered);
    saveDashboardConfig(reordered);
  };

  const resetLayout = () => {
    setWidgets(DEFAULT_WIDGETS);
    saveDashboardConfig(DEFAULT_WIDGETS);
    toast.success(sv("Layout återställd", "Layout reset"));
  };

  const visibleWidgets = widgets.filter(w => w.visible).sort((a, b) => a.order - b.order);

  const stats = [
    { title: sv("Aktiva projekt", "Active Projects"), value: summary?.projects?.active || 0, total: summary?.projects?.total, icon: Building2, color: "bg-blue-500", href: "/projects" },
    { title: sv("Uppgifter kvar", "Tasks Remaining"), value: summary?.tasks?.pending || 0, total: summary?.tasks?.total, icon: CheckSquare, color: "bg-green-500", href: "/projects" },
    { title: sv("Öppna avvikelser", "Open Incidents"), value: summary?.incidents?.open || 0, icon: AlertTriangle, color: "bg-red-500", href: "/incidents" },
    { title: sv("Väntande ÄTA", "Pending ÄTA"), value: summary?.change_orders?.pending || 0, icon: FileText, color: "bg-amber-500", href: "/change-orders" },
  ];

  const quickActions = [
    { icon: Plus, label: sv("Nytt projekt", "New project"), href: "/projects", color: "text-blue-600" },
    { icon: Clock, label: sv("Tidrapport", "Time report"), href: "/time-report", color: "text-green-600" },
    { icon: FileText, label: sv("Ny ÄTA", "New ÄTA"), href: "/change-orders", color: "text-amber-600" },
    { icon: AlertTriangle, label: sv("Avvikelse", "Incident"), href: "/incidents", color: "text-red-600" },
    { icon: Shield, label: "KMA", href: "/kma", color: "text-purple-600" },
  ];

  const sevColors = { critical: "bg-red-500", high: "bg-orange-500", medium: "bg-amber-400", low: "bg-blue-400" };
  const sevLabels = { critical: sv("Kritisk", "Critical"), high: sv("Hög", "High"), medium: sv("Medel", "Medium"), low: sv("Låg", "Low") };
  const bySeverity = summary?.incidents?.by_severity || { low: 0, medium: 0, high: 0, critical: 0 };
  const totalIncidents = Object.values(bySeverity).reduce((a, b) => a + b, 0);
  const budgetUtil = summary?.budget?.utilization || 0;
  const budgetColor = budgetUtil > 90 ? "text-red-600" : budgetUtil > 70 ? "text-amber-600" : "text-green-600";

  const widgetComponents = {
    stats: (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" key="stats">
        {stats.map((stat, i) => (
          <Link key={i} to={stat.href}>
            <Card className="hover:shadow-md transition-shadow cursor-pointer" data-testid={`stat-card-${i}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className={`p-2 rounded-lg ${stat.color}`}><stat.icon className="w-5 h-5 text-white" /></div>
                  {stat.total !== undefined && <span className="text-xs text-slate-500">/ {stat.total}</span>}
                </div>
                <div className="mt-3">
                  <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>{loading ? "-" : stat.value}</p>
                  <p className="text-sm text-slate-600 mt-0.5">{stat.title}</p>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
    ),

    quick_actions: (
      <Card key="quick_actions">
        <CardHeader className="pb-3"><CardTitle className="text-lg" style={{ fontFamily: 'Barlow Condensed' }}>{sv("Snabbåtgärder", "Quick Actions")}</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
            {quickActions.map((action, i) => (
              <Link key={i} to={action.href}>
                <div className="quick-action" data-testid={`quick-action-${i}`}>
                  <action.icon className={`quick-action-icon ${action.color}`} />
                  <span className="quick-action-label">{action.label}</span>
                </div>
              </Link>
            ))}
          </div>
        </CardContent>
      </Card>
    ),

    task_progress: (
      <Card key="task_progress">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg" style={{ fontFamily: 'Barlow Condensed' }}>{sv("Uppgiftsframsteg", "Task Progress")}</CardTitle>
            <TrendingUp className="w-5 h-5 text-slate-400" />
          </div>
        </CardHeader>
        <CardContent>
          {loading ? <div className="h-24 flex items-center justify-center"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div> : (
            <div>
              <div className="flex items-center justify-center mb-4">
                <div className="relative w-28 h-28">
                  <svg className="w-28 h-28 -rotate-90" viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r="42" fill="none" stroke="#e2e8f0" strokeWidth="10" />
                    <circle cx="50" cy="50" r="42" fill="none" stroke="#22c55e" strokeWidth="10"
                      strokeDasharray={`${(summary?.tasks?.completion_rate || 0) * 2.64} 264`} strokeLinecap="round" />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>{summary?.tasks?.completion_rate || 0}%</span>
                  </div>
                </div>
              </div>
              <div className="flex justify-between text-sm text-slate-600">
                <span>{summary?.tasks?.completed || 0} {sv("klara", "done")}</span>
                <span>{summary?.tasks?.pending || 0} {sv("kvar", "left")}</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    ),

    incidents: (
      <Card key="incidents">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg" style={{ fontFamily: 'Barlow Condensed' }}>{sv("Avvikelser", "Incidents")}</CardTitle>
            <AlertTriangle className="w-5 h-5 text-slate-400" />
          </div>
        </CardHeader>
        <CardContent>
          {loading ? <div className="h-24 flex items-center justify-center"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div> : totalIncidents === 0 ? (
            <div className="text-center py-4"><Shield className="w-10 h-10 text-green-300 mx-auto mb-2" /><p className="text-sm text-slate-500">{sv("Inga avvikelser", "No incidents")}</p></div>
          ) : (
            <div className="space-y-3">
              {["critical", "high", "medium", "low"].map(sev => {
                const count = bySeverity[sev] || 0;
                const pct = totalIncidents > 0 ? (count / totalIncidents * 100) : 0;
                return (
                  <div key={sev}>
                    <div className="flex items-center justify-between text-sm mb-1"><span className="text-slate-600">{sevLabels[sev]}</span><span className="font-medium text-slate-900">{count}</span></div>
                    <div className="w-full bg-slate-100 rounded-full h-2"><div className={`h-2 rounded-full ${sevColors[sev]} transition-all`} style={{ width: `${pct}%` }} /></div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    ),

    budget: (
      <Card key="budget">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg" style={{ fontFamily: 'Barlow Condensed' }}>{sv("Ekonomi", "Budget")}</CardTitle>
            <DollarSign className="w-5 h-5 text-slate-400" />
          </div>
        </CardHeader>
        <CardContent>
          {loading ? <div className="h-24 flex items-center justify-center"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div> : summary?.budget?.total_budget === 0 ? (
            <div className="text-center py-4"><DollarSign className="w-10 h-10 text-slate-300 mx-auto mb-2" /><p className="text-sm text-slate-500">{sv("Ingen budget satt", "No budget set")}</p></div>
          ) : (
            <div className="space-y-4">
              <div className="text-center">
                <p className={`text-3xl font-bold ${budgetColor}`} style={{ fontFamily: 'Barlow Condensed' }}>{budgetUtil}%</p>
                <p className="text-xs text-slate-500">{sv("av budget förbrukad", "of budget used")}</p>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-3"><div className={`h-3 rounded-full transition-all ${budgetUtil > 90 ? 'bg-red-500' : budgetUtil > 70 ? 'bg-amber-500' : 'bg-green-500'}`} style={{ width: `${Math.min(budgetUtil, 100)}%` }} /></div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><p className="text-slate-500">{sv("Budget", "Budget")}</p><p className="font-semibold">{(summary?.budget?.total_budget || 0).toLocaleString('sv-SE')} kr</p></div>
                <div><p className="text-slate-500">{sv("Förbrukat", "Spent")}</p><p className="font-semibold">{(summary?.budget?.total_actual || 0).toLocaleString('sv-SE')} kr</p></div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    ),

    time_week: (
      <Card key="time_week">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg" style={{ fontFamily: 'Barlow Condensed' }}>{sv("Tid denna vecka", "Time This Week")}</CardTitle>
            <Timer className="w-5 h-5 text-slate-400" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-2">
            <span className="text-4xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>{summary?.time?.this_week || 0}h</span>
            <span className="text-slate-500 mb-1">{sv("rapporterat", "reported")}</span>
          </div>
          <div className="mt-3 w-full bg-slate-100 rounded-full h-2"><div className="bg-blue-500 h-2 rounded-full transition-all" style={{ width: `${Math.min((summary?.time?.this_week || 0) / 40 * 100, 100)}%` }} /></div>
          <p className="text-xs text-slate-500 mt-1">{sv("av 40h arbetsvecka", "of 40h work week")}</p>
        </CardContent>
      </Card>
    ),

    recent_incidents: (
      <Card key="recent_incidents">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg" style={{ fontFamily: 'Barlow Condensed' }}>{sv("Senaste avvikelser", "Recent Incidents")}</CardTitle>
            <Link to="/incidents"><Button variant="ghost" size="sm">{sv("Visa alla", "View all")} <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
          </div>
        </CardHeader>
        <CardContent>
          {!summary?.incidents?.recent?.length ? (
            <p className="text-sm text-slate-500 text-center py-4">{sv("Inga senaste avvikelser", "No recent incidents")}</p>
          ) : (
            <div className="space-y-3">
              {summary.incidents.recent.map((inc, i) => (
                <div key={i} className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0">
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${sevColors[inc.severity] || 'bg-slate-300'}`} />
                  <div className="min-w-0 flex-1"><p className="text-sm font-medium text-slate-900 truncate">{inc.title}</p><p className="text-xs text-slate-500">{inc.project_name}</p></div>
                  <Badge variant="outline" className="text-xs flex-shrink-0">{inc.status}</Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    ),
  };

  // Determine layout: full-width vs grid items
  const fullWidthIds = ["stats", "quick_actions"];
  const gridItems = visibleWidgets.filter(w => !fullWidthIds.includes(w.id));
  const fullWidthItems = visibleWidgets.filter(w => fullWidthIds.includes(w.id));

  return (
    <AppLayout>
      <div className="p-4 lg:p-6 space-y-6 pb-20 lg:pb-6" data-testid="dashboard-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
              {t("welcome")}, {user?.name?.split(' ')[0] || 'User'}!
            </h1>
            <p className="text-slate-600 mt-1">{sv("Här är din översikt", "Here's your overview")}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setSettingsOpen(true)} data-testid="dashboard-settings-btn">
              <Settings className="w-4 h-4 mr-1.5" />
              {sv("Anpassa", "Customize")}
            </Button>
            <Link to="/projects">
              <Button className="btn-touch" data-testid="new-project-btn">
                <Plus className="w-5 h-5 mr-2" />{sv("Nytt projekt", "New project")}
              </Button>
            </Link>
          </div>
        </div>

        {/* Full-width widgets */}
        {fullWidthItems.map(w => widgetComponents[w.id])}

        {/* Grid widgets */}
        {gridItems.length > 0 && (
          <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {gridItems.map(w => widgetComponents[w.id])}
          </div>
        )}
      </div>

      {/* Settings Overlay */}
      {settingsOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={(e) => { if (e.target === e.currentTarget) setSettingsOpen(false); }}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 max-h-[80vh] flex flex-col" data-testid="dashboard-settings-panel">
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-slate-600" />
                <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
                  {sv("Anpassa dashboard", "Customize Dashboard")}
                </h3>
              </div>
              <button onClick={() => setSettingsOpen(false)} className="p-1 rounded-md hover:bg-slate-100" data-testid="close-settings-btn">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-1">
              {widgets.map((w, i) => (
                <div key={w.id} className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${w.visible ? 'bg-blue-50/50' : 'bg-slate-50'}`} data-testid={`widget-config-${w.id}`}>
                  <GripVertical className="w-4 h-4 text-slate-300 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className={`text-sm font-medium ${w.visible ? 'text-slate-800' : 'text-slate-400'}`}>
                      {language === "sv" ? w.label_sv : w.label_en}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => moveWidget(w.id, -1)} disabled={i === 0} className="p-1 rounded hover:bg-slate-200 disabled:opacity-30" data-testid={`widget-up-${w.id}`}>
                      <ChevronUp className="w-4 h-4 text-slate-500" />
                    </button>
                    <button onClick={() => moveWidget(w.id, 1)} disabled={i === widgets.length - 1} className="p-1 rounded hover:bg-slate-200 disabled:opacity-30" data-testid={`widget-down-${w.id}`}>
                      <ChevronDown className="w-4 h-4 text-slate-500" />
                    </button>
                  </div>
                  <Switch checked={w.visible} onCheckedChange={() => toggleWidget(w.id)} data-testid={`widget-toggle-${w.id}`} />
                </div>
              ))}
            </div>

            <div className="p-4 border-t flex justify-between">
              <Button variant="ghost" size="sm" onClick={resetLayout} data-testid="reset-layout-btn">
                {sv("Återställ standard", "Reset Default")}
              </Button>
              <Button size="sm" onClick={() => { setSettingsOpen(false); toast.success(sv("Layout sparad!", "Layout saved!")); }} data-testid="save-settings-btn">
                {sv("Klar", "Done")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
};
